import { Pencil } from "lucide-react";
import * as React from "react";

import { type Session, useUpdateDisplayName } from "@/api/auth";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { displayNameProblem } from "@/lib/display-name";

/**
 * The Settings "Display name" value, editable in place (flowstack#2002).
 *
 * Read mode shows the effective name and an Edit button; while an override is
 * in force it also shows the sign-in name with a "Reset to sign-in name"
 * control. Edit mode is a one-field form: Enter or Save sends it, Escape or
 * Cancel drops it, and focus returns to the Edit button either way. Saving an
 * empty value clears the override, as Reset does; saving the sign-in name
 * while no override is set pins it, so a later sign-in name change leaves it.
 *
 * The name is always rendered as text — React escapes it — never as HTML.
 */
export function DisplayNameField({ session }: { session: Session }): JSX.Element {
  const update = useUpdateDisplayName();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const [problem, setProblem] = React.useState<string | null>(null);
  const editButton = React.useRef<HTMLButtonElement>(null);
  const input = React.useRef<HTMLInputElement>(null);
  const returnFocus = React.useRef(false);
  const errorId = React.useId();
  const inputId = React.useId();

  React.useEffect(() => {
    if (editing) {
      input.current?.focus();
      input.current?.select();
    } else if (returnFocus.current) {
      returnFocus.current = false;
      editButton.current?.focus();
    }
  }, [editing]);

  const close = (): void => {
    returnFocus.current = true;
    setEditing(false);
    setProblem(null);
  };

  const open = (): void => {
    update.reset();
    setDraft(session.display_name);
    setProblem(null);
    setEditing(true);
  };

  const save = (event: React.FormEvent): void => {
    event.preventDefault();
    const name = draft.trim();
    const refused = displayNameProblem(name);
    if (refused) {
      setProblem(refused);
      return;
    }
    // Nothing to write: the override already holds this name, or there is no
    // override to clear. Saving the sign-in name with no override pins it.
    const unchanged = session.display_name_overridden ? name === session.display_name : name === "";
    if (unchanged) {
      close();
      return;
    }
    update.mutate(name, {
      onSuccess: close,
      onError: (err) => setProblem(errorMessage(err)),
    });
  };

  const reset = (): void => {
    setProblem(null);
    update.mutate(null, { onError: (err) => setProblem(errorMessage(err)) });
  };

  if (editing) {
    return (
      <form onSubmit={save} className="flex flex-col gap-2" noValidate>
        <label htmlFor={inputId} className="sr-only">
          Display name
        </label>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            id={inputId}
            ref={input}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (problem) setProblem(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                close();
              }
            }}
            autoComplete="name"
            aria-invalid={problem ? true : undefined}
            aria-describedby={problem ? errorId : undefined}
            disabled={update.isPending}
            className="h-9 sm:max-w-xs"
          />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={close}
              disabled={update.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
        {problem && (
          <p id={errorId} role="alert" className="text-xs text-destructive">
            {problem}
          </p>
        )}
      </form>
    );
  }

  const signInName = session.sign_in_display_name;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="break-words">{session.display_name}</span>
        <Button
          ref={editButton}
          type="button"
          variant="ghost"
          size="sm"
          onClick={open}
          aria-label="Edit display name"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground"
        >
          <Pencil className="h-3 w-3" aria-hidden />
          Edit
        </Button>
      </div>
      {session.display_name_overridden && signInName && (
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span>{`Sign-in name: ${signInName}`}</span>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={reset}
            disabled={update.isPending}
            className="h-auto p-0 text-xs"
          >
            Reset to sign-in name
          </Button>
        </div>
      )}
      {problem && (
        <p role="alert" className="text-xs text-destructive">
          {problem}
        </p>
      )}
    </div>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError && err.message) return err.message;
  return "Couldn't save the display name. Try again.";
}
